import type { Entrant, Heat, Round, TournamentState, TournamentStep } from '@sprinters/shared';
import type { RaceFrame } from '../game/SnapshotBuffer';

/** Identifiant du coureur correspondant à un joueur, s'il participe au tournoi. */
export function findMyEntrantId(tournament: TournamentState, playerId: string): string | null {
  for (const round of tournament.rounds) {
    for (const heat of round.heats) {
      const entrant = heat.lanes.find((lane) => lane.playerId === playerId);
      if (entrant) return entrant.id;
    }
  }
  return null;
}

export function findHeatOfEntrant(
  round: Round | undefined,
  entrantId: string | null,
): Heat | undefined {
  if (!round || !entrantId) return undefined;
  return round.heats.find((heat) => heat.lanes.some((lane) => lane.id === entrantId));
}

export function findLane(heat: Heat | undefined, entrantId: string | null): number {
  if (!heat || !entrantId) return -1;
  return heat.lanes.findIndex((lane) => lane.id === entrantId);
}

/** Reconstitue une image d'arrivée à partir des résultats d'une série terminée. */
export function frameFromResults(heat: Heat): RaceFrame | null {
  const results = heat.results;
  if (!results) return null;
  return {
    t: Math.max(0, ...results.map((result) => result.timeMs ?? 0)),
    runners: heat.lanes.map((entrant) => {
      const result = results.find((candidate) => candidate.entrantId === entrant.id);
      const finishTimeMs = result?.timeMs ?? null;
      return {
        x: result?.distanceM ?? 0,
        v: 0,
        phase: finishTimeMs !== null ? 'finished' : 'idle',
        falls: result?.falls ?? 0,
        finishTimeMs,
      };
    }),
  };
}

/** Texte du décompte selon le temps restant avant le départ. `null` une fois la course lancée. */
export function countdownText(goAt: number | null, now: number): string | null {
  if (goAt === null) return 'PRÊTS ?';
  const remaining = goAt - now;
  if (remaining > 3000) return 'PRÊTS ?';
  if (remaining > 2000) return '3';
  if (remaining > 1000) return '2';
  if (remaining > 0) return '1';
  if (remaining > -700) return 'PARTEZ !';
  return null;
}

export function isRacingStep(step: TournamentStep): boolean {
  return step === 'countdown' || step === 'racing';
}

export function stepLabel(step: TournamentStep): string {
  switch (step) {
    case 'briefing':
      return 'Présentation de la manche';
    case 'countdown':
      return 'Prenez place !';
    case 'racing':
      return 'Course en cours';
    case 'results':
      return 'Résultats';
    case 'finished':
      return 'Tournoi terminé';
  }
}

export function remainingSeconds(
  tournament: TournamentState,
  receivedAt: number,
  now: number,
): number | null {
  if (tournament.stepRemainingMs === null) return null;
  return Math.max(0, Math.ceil((tournament.stepRemainingMs - (now - receivedAt)) / 1000));
}

export function entrantById(heat: Heat, entrantId: string): Entrant | undefined {
  return heat.lanes.find((lane) => lane.id === entrantId);
}
